import { defineComponent as _defineComponent } from 'vue';
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useToolStore } from "@/stores/toolStore";
import { useUserStore } from "@/stores/userStore";
import localize from "@/utils/localization";
import { errorMessageAsString, rethrowSimple } from "@/utils/simple-error";
import { types_to_icons } from "./utilities";
import LoadingSpan from "../LoadingSpan.vue";
import FavoritesButton from "./Buttons/FavoritesButton.vue";
import PanelViewMenu from "./Menus/PanelViewMenu.vue";
import ToolBox from "./ToolBox.vue";
import Heading from "@/components/Common/Heading.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'ToolPanel',
    props: {
        workflow: { type: Boolean, default: false },
        dataManagers: { type: Array, default: null },
        moduleSections: { type: Array, default: null },
    },
    emits: ["onInsertTool", "onInsertModule", "onInsertWorkflow", "onInsertWorkflowSteps"],
    setup(__props, { emit }) {
        const props = __props;
        const userStore = useUserStore();
        const arePanelsFetched = ref(false);
        const toolStore = useToolStore();
        const { currentPanelView, defaultPanelView, isPanelPopulated, loading, panel, panelViews, currentPanel } = storeToRefs(toolStore);
        const loadingView = ref(undefined);
        const query = ref("");
        const showAdvanced = ref(false);
        const errorMessage = ref(undefined);
        initializeToolPanel();
        async function initializeToolPanel() {
            try {
                await toolStore.fetchPanelViews();
                await initializeTools();
            }
            catch (error) {
                console.error(error);
                errorMessage.value = errorMessageAsString(error);
            }
            finally {
                arePanelsFetched.value = true;
            }
        }
        watch(() => currentPanelView.value, () => {
            query.value = "";
        });
        // if currentPanelView ever becomes null || "", load tools
        watch(() => currentPanelView.value, async (newVal) => {
            if ((!newVal || !panel.value[newVal]) && arePanelsFetched.value) {
                await initializeTools();
            }
        });
        const toolPanelHeader = computed(() => {
            if (showAdvanced.value) {
                return localize("Advanced Tool Search");
            }
            else if (loading.value && loadingView.value) {
                return localize(loadingView.value);
            }
            else if (currentPanelView.value !== "default" &&
                panelViews.value &&
                panelViews.value[currentPanelView.value]?.name) {
                return localize(panelViews.value[currentPanelView.value]?.name);
            }
            else {
                return localize("Tools");
            }
        });
        const viewIcon = computed(() => {
            if (showAdvanced.value) {
                return "search";
            }
            else if (currentPanelView.value !== "default" &&
                panelViews.value &&
                typeof panelViews.value[currentPanelView.value]?.view_type === "string") {
                const viewType = panelViews.value[currentPanelView.value]?.view_type;
                return viewType ? types_to_icons[viewType] : null;
            }
            else {
                return null;
            }
        });
        const showFavorites = computed({
            get() {
                return query.value.includes("#favorites");
            },
            set(value) {
                if (value) {
                    if (!query.value.includes("#favorites")) {
                        query.value = `#favorites ${query.value}`.trim();
                    }
                }
                else {
                    query.value = query.value.replace("#favorites", "").trim();
                }
            },
        });
        async function initializeTools() {
            try {
                await userStore.loadUser(false);
                await toolStore.fetchTools();
                await toolStore.initCurrentPanelView(defaultPanelView.value);
            }
            catch (error) {
                console.error("ToolPanel - Intialize error:", error);
                errorMessage.value = errorMessageAsString(error);
                rethrowSimple(error);
            }
        }
        async function updatePanelView(panelView) {
            loadingView.value = panelViews.value[panelView]?.name;
            await toolStore.setCurrentPanelView(panelView);
            loadingView.value = undefined;
        }
        function onInsertTool(toolId, toolName) {
            emit("onInsertTool", toolId, toolName);
        }
        function onInsertModule(moduleName, moduleTitle) {
            emit("onInsertModule", moduleName, moduleTitle);
        }
        function onInsertWorkflow(workflowId, workflowName) {
            emit("onInsertWorkflow", workflowId, workflowName);
        }
        function onInsertWorkflowSteps(workflowId, workflowStepCount) {
            emit("onInsertWorkflowSteps", workflowId, workflowStepCount);
        }
        watch(() => query.value, (newQuery) => {
            showFavorites.value = newQuery.includes("#favorites");
        });
        return { __sfc: true, userStore, props, emit, arePanelsFetched, toolStore, currentPanelView, defaultPanelView, isPanelPopulated, loading, panel, panelViews, currentPanel, loadingView, query, showAdvanced, errorMessage, initializeToolPanel, toolPanelHeader, viewIcon, showFavorites, initializeTools, updatePanelView, onInsertTool, onInsertModule, onInsertWorkflow, onInsertWorkflowSteps, faCaretDown, FontAwesomeIcon, LoadingSpan, FavoritesButton, PanelViewMenu, ToolBox, Heading };
    }
});
